import { Card } from "@coherence-design-system/controls";
import { PrimaryButton, Spinner, SpinnerSize } from "@fluentui/react";
import React, { useContext, useEffect, useState } from "react";
import "./DPRProforma.css";
import "./JapanQuotationTemplate.css";
import { makePdf } from "../PrintQuotation/PrintQuotation";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { QuotationContext } from "../QuotationContext";
import { QuotationInfoType } from "../Quotation.types";
import { CountryProfileService } from "../../../services/CountryProfiles/CountryProfileService";
import { useNavigate } from "react-router-dom";
import { priceWithCurrencyForJapan, symbolForCurrency } from "../../../Utils/PriceWithCurrency";
import { CountryProfileInfo } from "../../CountriesProfile/CountriesProfile.type";
import { IExtendedDetailItemEntity } from "../Common/Interfaces/IItemEntity";
import { formattedPriceForJapan } from "../../../Utils/FormattedPrice";
import { formatDateWithPattern, getCountryDateFormat } from "../../../Utils/DateFormatUtility";

// Helper function for country-specific date formatting
const formatDateForCountry = async (dateStr: string | undefined, countryName: string | undefined): Promise<string> => {
  if (!dateStr) return '';
  
  try {
    // Parse the date string
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date string: ${dateStr}`);
      return dateStr || '';
    }
    
    // For Japan, the default format should be YYYY/MM/DD
    let dateFormat = 'YYYY/MM/DD'; // Default format for Japan
    if (countryName) {
      const countryFormat = await getCountryDateFormat(countryName);
      if (countryFormat) {
        dateFormat = countryFormat;
      }
    }
    
    // Format the date according to country preference
    return formatDateWithPattern(date, dateFormat);
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateStr || '';
  }
};

// Japanese language labels for DPR (Downpayment Request)
const JapaneseDPRLabels = {
  // Header
  downpaymentRequest: "頭金支払い請求書",
  microsoft: "Microsoft",
  
  // Company Info
  companyInfo: {
    name: "日本マイクロソフト株式会社",
    address1: "品川グランドセントラルタワー",
    address2: "東京都港区港南2-16-3",
    address3: "〒108-0075 東京都",
    taxId: "登録番号"
  },
  
  // DPR Details
  dprDetails: {
    pageNumber: "ページ番号",
    paymentReference: "支払い参照番号",
    dueDate: "支払期限",
    requestNumber: "リクエスト番号",
    requestDate: "発行日",
    quotationNumber: "見積番号"
  },
  
  // Customer Information
  customerInfo: {
    billTo: "請求先",
    shipTo: "配送先", 
    companyName: "会社名",
    businessPartner: "ビジネスパートナー番号",
    customerNumber: "お客様番号",
    customerGstId: "顧客消費税ID",
    customerPo: "お客様注文番号",
    customer: "顧客",
    customerTaxId: "顧客税ID",
    soldToId: "販売先ID"
  },
  
  // Product Table Headers
  productTable: {
    itemNumber: "品目番号",
    description: "製品名",
    quantity: "数量",
    Price: "価格",
    extendedLineTax: "消費税額",
    lineAmount: "合計",
    unitPrice: "単価",
    tax: "税額"
  },
  
  // Summary Labels
  summary: {
    subtotal: "小計:",
    gst: "消費税:",
    totalTax: "税額合計:",
    taxAmount: "税額",
    taxRate: "税率",
    taxAmountWithRate: "税額 (税率):",
    grandTotal: "合計金額:",
    downpaymentAmount: "頭金金額:",
    remainingAmount: "残額:"
  },
  
  // Payment Information
  payment: {
    paymentTerms: "支払条件",
    paymentMethod: "支払方法",
    bankDetails: "銀行詳細",
    wireTransfer: "銀行振込",
    wireTransferOption: "銀行振込の支払いオプション：",
    bankName: "銀行名：",
    bankAddress: "銀行住所：",
    bankPhone: "銀行電話番号：",
    addressLine1: "住所1：",
    addressLine2: "住所2：",
    addressLine3: "住所3：",
    accountName: "口座名義：",
    accountAddress: "口座住所：",
    accountNumber: "口座番号：",
    ibanNumber: "IBAN番号：",
    swiftCode: "スイフトコード：",
    swiftBicCode: "スイフト/BICコード：",
    switchCode: "スイッチコード：",
    sortCode: "ソートコード：",
    correspondingBank: "コルレス銀行：",
    vat: "TAX：",
    correspondingBankSwiftBicCode: "コルレス銀行スイフト/BICコード：",
    bankKey: "銀行キー：",
    bcCode: "BCコード：",
    currencyCode: "通貨コード：",
    companyCode: "会社コード：",
    remittanceNote: "Microsoftへの送金の際は、迅速な決済のため顧客番号をご参照ください。"
  },
  
  // Comments and Footer
  footer: {
    comments: "コメント:",
    paymentAppreciation: "お支払いにつきまして、下記の情報をご利用いただき、迅速なご対応を賜りますようお願い申し上げます。なお、現在は「小切手によるお支払い」はご利用いただけませんので、予めご了承ください。ご入金が確認でき次第、速やかに商品を発送させていただきます。この度は弊社をご利用いただき、誠にありがとうございます。",
    creditCardTitle: "クレジットカード支払いオプション：",
    creditCardInstructions: "クレジットカードでの支払いが希望の支払い方法であることを販売者にお伝えいただいた場合は、提供されたリンクを使用して支払いを完了してください：",
    termsConditions: "利用規約：",
    termsInstructionsWireTransfer: "支払い条件については、購入契約書をご参照ください。受け入れ可能な支払い方法は銀行振込です。支払いは電子資金振替のみで行ってください。この請求書に記載されている住所への物理的な支払いは送付しないでください。支払いが遅れたり不完全な場合は、罰則が科される可能性があります。この請求書についてご質問がある場合は、Retail Stores Invoice サポートまでメールでお問い合わせください：",
    termsInstructionsCreditCard: "支払い条件については、購入契約書をご参照ください。受け入れ可能な支払い方法はクレジットカードです。この請求書に記載されている住所への物理的な支払いは送付しないでください。支払いが遅れたり不完全な場合は、罰則が科される可能性があります。この請求書についてご質問がある場合は、Retail Stores Invoice サポートまでメールでお問い合わせください：",
    supportEmail: "rsinvsup@microsoft.com"
  },
  
  // Buttons
  buttons: {
    downloadPdf: "DPR PDF をダウンロード",
    print: "印刷",
    back: "戻る"
  }
};

export const JapanDPRProforma: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const appInsights = useAppInsightsContext();
  const [isLoading, setIsLoading] = useState(false);
  const [termsAndConditionsDocument, settermsAndConditionsDocument] = useState(new Uint8Array());
  const [termsAndConditionsDocumentId, settermsAndConditionsDocumentId] = useState('');
  const [countryProfileInfo, setCountryProfileInfo] = useState<CountryProfileInfo>();
  const { quotationInfo } = useContext(QuotationContext) as QuotationInfoType;
  
  // State for formatted dates
  const [formattedQuotationDate, setFormattedQuotationDate] = useState<string>('');
  
  // Format dates according to Japan's preferred date format
  useEffect(() => {
    const formatDates = async () => {
      try {
        const countryName = "Japan"; // Hard-coded for this template
        
        // Format quotation date
        if (quotationInfo?.quotationDate) {
          const formatted = await formatDateForCountry(quotationInfo.quotationDate, countryName);
          setFormattedQuotationDate(formatted);
        }
      } catch (error) {
        console.error('Error formatting dates:', error);
      }
    };

    formatDates();
  }, [quotationInfo?.quotationDate]);

  const lineItems: IExtendedDetailItemEntity[] =
    quotationInfo?.bundleItems?.length > 0
      ? ([
          ...quotationInfo?.standaloneItems,
          ...quotationInfo?.bundleItems
        ] as IExtendedDetailItemEntity[])
      : quotationInfo?.standaloneItems?.length > 0
        ? ([
            ...quotationInfo?.standaloneItems,
            ...quotationInfo.bundleItems,
          ] as IExtendedDetailItemEntity[])
        : [];

  const pdfFileName = `${quotationInfo?.quotationNumber}_Japan_DPR`;

  const PrintHtmlToPdf = async () => {
    appInsights.trackEvent({
      name: "In JapanDPRProforma.tsx, Clicked Print button to download the Japan DPR Proforma",
    });
    if (termsAndConditionsDocument.byteLength === 0 && termsAndConditionsDocumentId) {
      await CountryProfileService.getCountryProfileDocument(termsAndConditionsDocumentId)
        .then((res) => {
          appInsights.trackEvent({
            name: "In JapanDPRProforma.tsx, Country Terms and Condition document fetched successfully."
          });
          if (res.data === null) {
            makePdf(appInsights, pdfFileName, lineItems.length, termsAndConditionsDocument);
          }
          settermsAndConditionsDocument(res.data);
        })
        .catch((error) => {
          makePdf(appInsights, pdfFileName, lineItems.length, termsAndConditionsDocument);
          appInsights.trackException({
            error,
            properties: {
              name: `In JapanDPRProforma.tsx, error while fetching termsAndCondition document.`,
            },
          });
        });
    } else {
      makePdf(appInsights, pdfFileName, lineItems.length, termsAndConditionsDocument);
    }
  };

  const backButtonClick = async () => {
    appInsights.trackEvent({
      name: "In JapanDPRProforma.tsx, Clicked Back button.",
      properties: { quotationId: quotationInfo.quotationId },
    });
    navigate("/QuotationDetail", {
      state: {
        searchText: quotationInfo.quotationId,
        partitionKey: quotationInfo.partitionKey,
      },
    });
  };

  useEffect(() => {
    async function PrintPdf() {
      try {
        if (termsAndConditionsDocument.byteLength > 0) {
          setIsLoading(true);
          await makePdf(appInsights, pdfFileName, lineItems.length, termsAndConditionsDocument);
          setIsLoading(false);
          settermsAndConditionsDocument(new Uint8Array());
        }
      } catch (error: any) {
        setIsLoading(false);
        appInsights.trackException({
          error,
          properties: {
            name: "In JapanDPRProforma.tsx, error while printing the PDF.",
          },
        });
      }
    }
    PrintPdf();
  }, [termsAndConditionsDocument.byteLength]);

  useEffect(() => {
    setIsLoading(true);
    CountryProfileService.getCountryProfileByCountryName(quotationInfo.country)
      .then(async (res) => {
        if (res !== undefined) {
          settermsAndConditionsDocumentId(
            res.data.data.data.termsAndConditions[
              res.data.data.data.termsAndConditions?.length - 1
            ]?.id
          );
          setCountryProfileInfo(res.data.data.data);
          appInsights.trackEvent({
            name: "In JapanDPRProforma.tsx, country detail fetched successfully."
          });
          setIsLoading(false);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        appInsights.trackException({
          error,
          properties: {
            name: "In JapanDPRProforma.tsx, error while getting all country detail.",
          },
        });
      });
  }, []);

  if (isLoading) {
    return <Spinner size={SpinnerSize.large} />;
  }

  return (
    <div className="japan-dpr-template">
      {quotationInfo ? (
        <>
          <div className="top30">
            <PrimaryButton className="float-right" onClick={PrintHtmlToPdf}>
              {JapaneseDPRLabels.buttons.downloadPdf}
            </PrimaryButton>
            <PrimaryButton className="float-right" onClick={backButtonClick}>
              {JapaneseDPRLabels.buttons.back}
            </PrimaryButton>
          </div>
          <div style={{ padding: "40px 40px" }}>
            <div id={pdfFileName}>
              {/* Header Section */}
              <div className={`flex space-between ${window.innerWidth > 750 ? 'flexDirectionRow' : 'flexDirectionColumn'} width100`}>
                <div>
                  <div>
                    <div style={{ marginBottom: "20px" }}>
                      <img
                        src="/microsoft-logo.png"
                        className="invlogo"
                        style={{ width: "140px" }}
                        alt="Microsoft logo"
                      />
                    </div>
                    <div>
                      <p className="pb-1">{JapaneseDPRLabels.companyInfo.name}</p>
                      <p>{JapaneseDPRLabels.companyInfo.address1}</p>
                      <p className="pb-1">{JapaneseDPRLabels.companyInfo.address2}</p>
                      <p className="pb-1">{JapaneseDPRLabels.companyInfo.address3}</p>
                    </div>
                  </div>
                </div>
                <div>
                  <h2
                    className="pb-1"
                    style={{ textAlign: "right", marginTop: "30px" }}
                  >
                    {JapaneseDPRLabels.downpaymentRequest}
                  </h2>
                  <div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "start",
                        flexDirection: "column",
                      }}
                    >
                      <p className="pb-1">
                        <strong>{JapaneseDPRLabels.dprDetails.requestDate}:</strong>
                        {` ${formattedQuotationDate || quotationInfo?.quotationDate}`}
                      </p>
                      <p className="pb-1">
                        <strong>{JapaneseDPRLabels.dprDetails.quotationNumber}:</strong>
                        {` ${quotationInfo?.quotationNumber}`}
                      </p>
                      <p className="pb-1">
                        <strong>{JapaneseDPRLabels.customerInfo.customerPo}:</strong>
                        {` ${quotationInfo?.customerPurchaseOrder || ""}`}
                      </p>
                      <p className="pb-1">
                        <strong>{JapaneseDPRLabels.dprDetails.pageNumber}</strong>
                        {` 1 of 1`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="horizontalLine"></div>

              {/* Customer Information Section */}
              <div className={`top30 flex space-between ${window.innerWidth > 750 ? 'flexDirectionRow' : 'flexDirectionColumn'}`}>
                <div className={`${window.innerWidth > 750 ? 'width33' : ''}`}>
                  <table className="table-no-border">
                    <tbody>
                      <tr>
                        <td className="td-border">
                          <p className="pb-1"><strong>{JapaneseDPRLabels.customerInfo.billTo}:</strong></p>
                          <p className="pb-1">{quotationInfo?.customer?.selectedBillToAddress?.name}</p>
                          <p className="pb-1">{quotationInfo?.customer?.selectedBillToAddress?.street}</p>
                          <p className="pb-1">
                            {[
                              quotationInfo?.customer?.selectedBillToAddress?.city,
                              quotationInfo?.customer?.selectedBillToAddress?.province || quotationInfo?.customer?.selectedBillToAddress?.state,
                              quotationInfo?.customer?.selectedBillToAddress?.postalCode
                            ].filter(Boolean).join(', ')}
                          </p>                 
                          <p className="pb-1">{quotationInfo?.customer?.selectedBillToAddress?.country}</p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                <div className={`${window.innerWidth > 750 ? 'width33' : ''}`}>
                  <p>
                    <strong>顧客:</strong> {quotationInfo?.customer?.accountId}
                  </p>
                  <p>
                    <strong>{JapaneseDPRLabels.customerInfo.customerTaxId}:</strong> {quotationInfo?.customer?.taxId || ""}
                  </p>
                  <p>
                    <strong>{JapaneseDPRLabels.customerInfo.soldToId}:</strong> {quotationInfo?.customer?.businessAddress?.sapAccountId || ""}
                  </p>
                </div>

                <div className={`${window.innerWidth > 750 ? 'width33' : ''}`}>
                  <table className="table-no-border">
                    <tbody>
                      <tr>
                        <td className="td-border">
                          <p className="pb-1"><strong>{JapaneseDPRLabels.customerInfo.shipTo}:</strong></p>
                          <p className="pb-1">{quotationInfo?.customer?.selectedShipToAddress?.name}</p>
                          <p className="pb-1">{quotationInfo?.customer?.selectedShipToAddress?.street}</p>
                          <p className="pb-1">{[quotationInfo?.customer?.selectedShipToAddress?.city, quotationInfo?.customer?.selectedShipToAddress?.province || quotationInfo?.customer?.selectedShipToAddress?.state, quotationInfo?.customer?.selectedShipToAddress?.postalCode].filter(Boolean).join(', ')}</p>                 
                          <p className="pb-1">{quotationInfo?.customer?.selectedShipToAddress?.country}</p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Product Table */}
              <div className="top30">
                <table className="table border-color">
                  <thead>
                    <tr className="table-header">
                      <th style={{ textAlign: "left", padding: "10px" }}>#</th>
                      <th style={{ textAlign: "left", padding: "10px" }}>
                        {JapaneseDPRLabels.productTable.description}
                      </th>
                      <th style={{ textAlign: "center", padding: "10px" }}>
                        {JapaneseDPRLabels.productTable.quantity}
                      </th>
                      <th className="text-right" style={{ padding: "10px" }}>
                        {JapaneseDPRLabels.productTable.Price}
                      </th>
                      <th className="text-right" style={{ padding: "10px" }}>
                        {JapaneseDPRLabels.productTable.extendedLineTax}
                      </th>
                      <th className="text-right" style={{ padding: "10px" }}>
                        {JapaneseDPRLabels.productTable.lineAmount}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems?.map((product, index) => (
                      <tr key={index}>
                        <td style={{ textAlign: "left", padding: "8px" }}>{index + 1}</td>
                        <td style={{ textAlign: "left", padding: "8px" }}>
                          {product.sapItemId ? `[${product.sapItemId}] ${product.productTitle}` : product.productTitle}
                        </td>
                        <td style={{ textAlign: "center", padding: "8px" }}>
                          {product.quantity}
                        </td>
                        <td className="text-right" style={{ padding: "8px" }}>
                          {formattedPriceForJapan(product.msrp)}
                        </td>
                        <td className="text-right" style={{ padding: "8px" }}>
                          {formattedPriceForJapan(product.lineItemTax)}
                        </td>
                        <td className="text-right" style={{ padding: "8px" }}>
                          {formattedPriceForJapan(product.lineTotal)}
                        </td>
                      </tr>
                    ))}
                    
                  </tbody>
                </table>
                
                {/* Summary Section - Outside of Table */}
                <div style={{ marginTop: "20px", textAlign: "right" }}>
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "10px" }}>
                    <div style={{ width: "350px", display: "flex" }}>
                      <span style={{ width: "180px", textAlign: "right" }}>小計</span>
                      <span style={{ width: "20px", textAlign: "center" }}>:</span>
                      <span style={{ flex: 1, textAlign: "right" }}>{priceWithCurrencyForJapan(quotationInfo?.quoteCurrency, quotationInfo?.subTotal)}</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "10px" }}>
                    <div style={{ width: "350px", display: "flex" }}>
                      <span style={{ width: "180px", textAlign: "right" }}>税額 (税率)</span>
                      <span style={{ width: "20px", textAlign: "center" }}>:</span>
                      <span style={{ flex: 1, textAlign: "right" }}>{priceWithCurrencyForJapan(quotationInfo?.quoteCurrency, quotationInfo?.totalTax)}{" "}({(lineItems.find(item => item?.effectiveTaxRate > 0)?.effectiveTaxRate || 0) * 100}%)</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "10px" }}>
                    <div style={{ width: "350px", display: "flex" }}>
                      <span style={{ width: "180px", textAlign: "right" }}>合計金額</span>
                      <span style={{ width: "20px", textAlign: "center" }}>:</span>
                      <span style={{ flex: 1, textAlign: "right" }}>{priceWithCurrencyForJapan(quotationInfo?.quoteCurrency, quotationInfo?.total)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Comments Section */}
              <div style={{ marginTop: "50px" }}>
                <div style={{ marginBottom: "20px" }}>
                  <p><strong>{JapaneseDPRLabels.footer.comments}</strong></p>
                  <p style={{ marginTop: "10px", lineHeight: "1.6" }}>
                    {JapaneseDPRLabels.footer.paymentAppreciation}
                  </p>
                </div>

                {/* Conditional Payment Sections - Japanese Language */}
                {quotationInfo?.paymentMethod === "WireTransfer" ? (
                  <>
                    <div style={{ marginTop: "10px" }}>
                      <p className="pb-5">
                        <strong>{JapaneseDPRLabels.payment.wireTransferOption}</strong>
                      </p>
                    </div>
                    <div>
                      <div>
                        <div>
                          {countryProfileInfo?.bankName && (
                            <p className="pb-5">
                              <b>{JapaneseDPRLabels.payment.bankName}</b>
                              {`${countryProfileInfo?.bankName}`}
                            </p>
                          )}
                          {countryProfileInfo?.bankAddress && (
                            <p className="pb-5">
                              <b>{JapaneseDPRLabels.payment.bankAddress}</b>
                              {`${countryProfileInfo?.bankAddress}`}
                            </p>
                          )}
                          {countryProfileInfo?.bankPhone && (
                            <p className="pb-5">
                              <b>{JapaneseDPRLabels.payment.bankPhone}</b>
                              {`${countryProfileInfo?.bankPhone}`}
                            </p>
                          )}
                          {countryProfileInfo?.addressLine1 && (
                            <p className="pb-5">
                              <b>{JapaneseDPRLabels.payment.addressLine1}</b>
                              {`${countryProfileInfo?.addressLine1}`}
                            </p>
                          )}
                          {countryProfileInfo?.addressLine2 && (
                            <p className="pb-5">
                              <b>{JapaneseDPRLabels.payment.addressLine2}</b>
                              {`${countryProfileInfo?.addressLine2}`}
                            </p>
                          )}
                          {countryProfileInfo?.addressLine3 && (
                            <p className="pb-5">
                              <b>{JapaneseDPRLabels.payment.addressLine3}</b>
                              {`${countryProfileInfo?.addressLine3}`}
                            </p>
                          )}
                          {countryProfileInfo?.accountName && (
                            <p className="pb-5">
                              <b>{JapaneseDPRLabels.payment.accountName}</b>
                              {`${countryProfileInfo?.accountName}`}
                            </p>
                          )}
                          {countryProfileInfo?.accountAddress && (
                            <p className="pb-5">
                              <b>{JapaneseDPRLabels.payment.accountAddress}</b>
                              {`${countryProfileInfo?.accountAddress}`}
                            </p>
                          )}
                          {countryProfileInfo?.accountNumber && (
                            <p className="pb-5">
                              <b>{JapaneseDPRLabels.payment.accountNumber}</b>
                              {`${countryProfileInfo?.accountNumber}`}
                            </p>
                          )}
                          {countryProfileInfo?.iban && (
                            <p className="pb-5">
                              <b>{JapaneseDPRLabels.payment.ibanNumber}</b>
                              {`${countryProfileInfo?.iban}`}
                            </p>
                          )}
                          {countryProfileInfo?.swiftCode && (
                            <p className="pb-5">
                              <b>{JapaneseDPRLabels.payment.swiftCode}</b>
                              {`${countryProfileInfo?.swiftCode}`}
                            </p>
                          )}
                          {countryProfileInfo?.swiftBicCode && (
                            <p className="pb-5">
                              <b>{JapaneseDPRLabels.payment.swiftBicCode}</b>
                              {`${countryProfileInfo?.swiftBicCode}`}
                            </p>
                          )}
                          {countryProfileInfo?.switchCode && (
                            <p className="pb-5">
                              <b>{JapaneseDPRLabels.payment.switchCode}</b>
                              {`${countryProfileInfo?.switchCode}`}
                            </p>
                          )}
                          {countryProfileInfo?.sortCode && (
                            <p className="pb-5">
                              <b>{JapaneseDPRLabels.payment.sortCode}</b>
                              {`${countryProfileInfo?.sortCode}`}
                            </p>
                          )}
                          {countryProfileInfo?.correspondingBank && (
                            <p className="pb-5">
                              <b>{JapaneseDPRLabels.payment.correspondingBank}</b>
                              {`${countryProfileInfo?.correspondingBank}`}
                            </p>
                          )}
                          {countryProfileInfo?.vatId && (
                            <p className="pb-5">
                              <b>{JapaneseDPRLabels.payment.vat}</b>
                              {`${countryProfileInfo?.vatId}`}
                            </p>
                          )}
                          {countryProfileInfo?.correspondingBankSwiftBICCode && (
                            <p className="pb-5">
                              <b>{JapaneseDPRLabels.payment.correspondingBankSwiftBicCode}</b>
                              {`${countryProfileInfo?.correspondingBankSwiftBICCode}`}
                            </p>
                          )}
                          {countryProfileInfo?.bankKey && (
                            <p className="pb-5">
                              <b>{JapaneseDPRLabels.payment.bankKey}</b>
                              {`${countryProfileInfo?.bankKey}`}
                            </p>
                          )}
                          {countryProfileInfo?.bcCode && (
                            <p className="pb-5">
                              <b>{JapaneseDPRLabels.payment.bcCode}</b>
                              {`${countryProfileInfo?.bcCode}`}
                            </p>
                          )}
                          {countryProfileInfo?.remittanceCurrencyCode && (
                            <p className="pb-5">
                              <b>{JapaneseDPRLabels.payment.currencyCode}</b>
                              {`${countryProfileInfo?.remittanceCurrencyCode}`}
                            </p>
                          )}
                          {countryProfileInfo?.remittanceCompanyCode && (
                            <p className="pb-5">
                              <b>{JapaneseDPRLabels.payment.companyCode}</b>
                              {`${countryProfileInfo?.remittanceCompanyCode}`}
                            </p>
                          )}
                        </div>
                      </div>
                      <p className="pb-1">
                        {JapaneseDPRLabels.payment.remittanceNote}
                      </p>
                    </div>
                    <div style={{ marginTop: "10px", paddingBottom: "50px" }}>
                      <div>
                        <div>
                          <p className="pb-5">
                            <strong>{JapaneseDPRLabels.footer.termsConditions}</strong>
                          </p>
                          <p className="pb-1">
                            {JapaneseDPRLabels.footer.termsInstructionsWireTransfer}&nbsp;
                            <a href="mailto:rsinvsup@microsoft.com">{JapaneseDPRLabels.footer.supportEmail}</a>
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ marginTop: "10px" }}>
                      <div>
                        <div>
                          <p className="pb-1">
                            <b>{JapaneseDPRLabels.footer.creditCardTitle}</b>
                          </p>
                          <p className="pb-1">
                            {JapaneseDPRLabels.footer.creditCardInstructions}&nbsp;
                            <a
                              href={quotationInfo.adyenPaymentLink?.startsWith('http') 
                                ? quotationInfo.adyenPaymentLink 
                                : `https://${quotationInfo.adyenPaymentLink}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "#0078d4", textDecoration: "underline" }}
                            >
                              {quotationInfo.adyenPaymentLink}
                            </a>
                          </p>
                        </div>
                      </div>
                    </div>
                    <div style={{ marginTop: "10px" }}>
                      <div>
                        <div>
                          <p className="pb-1">
                            <b>{JapaneseDPRLabels.footer.termsConditions}</b>
                          </p>
                          <p className="pb-1">
                            {JapaneseDPRLabels.footer.termsInstructionsCreditCard}&nbsp;
                            <a href="mailto:rsinvsup@microsoft.com">{JapaneseDPRLabels.footer.supportEmail}</a>
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <Spinner size={SpinnerSize.large} />
      )}
    </div>
  );
};
